import './Breadcrumbs.css';
import type { Geography } from '../../contracts/static';
import { t } from '../../copy/strings';
import { placeName } from '../../format/places';
import type { PlaceRoute } from '../../model/types';
import { closePlace, openPlace } from '../../state/actions';
import { useRouteState, useStatics } from '../../state/hooks';

/** Um item das migalhas: o texto e o lugar ao qual ele leva (o último é o atual, sem destino). */
export interface Crumb {
  label: string;
  /** Lugar ao clicar; null no item atual. */
  to: PlaceRoute | null;
}

const BR: PlaceRoute = { kind: 'br' };

/** Nome do município pelo cadastro (o código, enquanto a geografia não chega). */
function municipalityName(ibge: string, geo: Geography | null): string {
  return geo?.municipalities.find((m) => m.ibge === ibge)?.name ?? ibge;
}

function country(iso2: string, geo: Geography | null) {
  return geo?.countries.find((c) => c.iso2.toUpperCase() === iso2.toUpperCase()) ?? null;
}

/**
 * Migalhas de §9.3 por rota: `br` → nenhuma; `uf` → Brasil › {UF}; município → Brasil › {UF} › {Município};
 * Exterior → Brasil › Exterior; país → Brasil › Exterior › {País}; cidade → Brasil › Exterior › {País} › {Cidade},
 * com o país só quando ele tem mais de 1 cidade (AC-9.3-07).
 */
export function crumbsFor(place: PlaceRoute, geo: Geography | null): Crumb[] {
  const brasil: Crumb = { label: t('map.crumbs.br'), to: BR };
  const abroad: Crumb = { label: t('map.crumbs.abroad'), to: { kind: 'abroad' } };
  let list: Crumb[];
  switch (place.kind) {
    case 'br':
      return [];
    case 'uf':
      list = [brasil, { label: placeName(place.uf), to: null }];
      break;
    case 'municipality':
      list = [
        brasil,
        { label: placeName(place.uf), to: { kind: 'uf', uf: place.uf } },
        { label: municipalityName(place.ibge, geo), to: null },
      ];
      break;
    case 'abroad':
      list = [brasil, { label: t('map.crumbs.abroad'), to: null }];
      break;
    case 'country':
      list = [brasil, abroad, { label: country(place.iso2, geo)?.name ?? place.iso2, to: null }];
      break;
    case 'abroad_city': {
      const c = country(place.iso2, geo);
      const city = geo?.abroad_cities.find((x) => x.tse === place.tse);
      list = [brasil, abroad];
      if (c && c.n_cities > 1) list.push({ label: c.name, to: { kind: 'country', iso2: place.iso2 } });
      list.push({ label: city?.name ?? place.tse, to: null });
      break;
    }
  }
  return list;
}

function go(to: PlaceRoute): void {
  if (to.kind === 'br') closePlace();
  else openPlace(to);
}

export interface BreadcrumbsProps {
  /** TV: migalhas inertes (sem ponteiro e fora da ordem de Tab, §9.3 estado TV). */
  inert?: boolean;
}

/**
 * "Onde você está" (§9.3): `<nav>` + `<ol>` com o caminho do lugar em foco; cada item anterior sobe até ele com
 * `openPlace`/`closePlace` (cargo, modo do mapa e `t` preservados pelo roteador, AC-9.3-06). Oculto no Brasil.
 */
export function Breadcrumbs({ inert = false }: BreadcrumbsProps) {
  const route = useRouteState();
  const geo = useStatics().geography;
  const items = crumbsFor(route.place, geo);
  if (items.length === 0) return null;
  return (
    <nav
      class={`breadcrumbs${inert ? ' breadcrumbs--inert' : ''}`}
      aria-label={t('map.crumbs.aria')}
      data-testid="breadcrumbs"
    >
      <ol class="breadcrumbs__list">
        {items.map((item, i) => (
          <li key={`${i}:${item.label}`} class="breadcrumbs__item">
            {i > 0 ? (
              <span class="breadcrumbs__sep" aria-hidden="true">
                ›
              </span>
            ) : null}
            {item.to === null ? (
              <span class="breadcrumbs__current" aria-current="page">
                {item.label}
              </span>
            ) : (
              <button
                type="button"
                class="breadcrumbs__link"
                tabIndex={inert ? -1 : undefined}
                onClick={() => {
                  if (!inert && item.to) go(item.to);
                }}
              >
                {item.label}
              </button>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
}
